import { useEffect, useState, useMemo } from "react";
import { fetchArticles, fetchParties } from "@/lib/api";
import Seo from "@/components/Seo";
import ArticleCard from "@/components/ArticleCard";
import { Newspaper, AlertTriangle, Flame } from "lucide-react";

const TYPES = [
    { value: "", label: "Alle typer" },
    { value: "skandale", label: "Skandaler" },
    { value: "løgn", label: "Løgne" },
    { value: "valgflæsk", label: "Valgflæsk" },
    { value: "løfte", label: "Holdte løfter" },
    { value: "generel", label: "Generel omtale" },
];

const SOURCES = [
    { key: "", label: "Alle kilder", domain: null },
    { key: "Ekstra Bladet", label: "Ekstra Bladet (eb.dk)", domain: "ekstrabladet.dk" },
    { key: "B.T.", label: "B.T. (bt.dk)", domain: "bt.dk" },
    { key: "TV 2 Politik", label: "TV 2 Politik (tv2.dk)", domain: "tv2.dk" },
    { key: "Politiken", label: "Politiken (politiken.dk)", domain: "politiken.dk" },
    { key: "DR Nyheder", label: "DR Nyheder (dr.dk)", domain: "dr.dk" },
];

export default function Articles() {
    const [articles, setArticles] = useState([]);
    const [parties, setParties] = useState([]);
    const [type, setType] = useState("");
    const [sourceKey, setSourceKey] = useState("");
    const [partyId, setPartyId] = useState("");

    useEffect(() => {
        fetchParties().then(setParties);
    }, []);

    useEffect(() => {
        const params = {};
        if (type) params.type = type;
        if (partyId) params.party_id = partyId;
        fetchArticles(params).then(setArticles);
    }, [type, partyId]);

    const partiesById = useMemo(
        () => Object.fromEntries(parties.map((p) => [p.id, p])),
        [parties]
    );

    const filtered = sourceKey
        ? articles.filter((a) => a.source === sourceKey)
        : articles;

    const lieCount = articles.filter((a) => a.type === "løgn").length;
    const valgflaeskCount = articles.filter((a) => a.type === "valgflæsk").length;
    const scandalCount = articles.filter((a) => a.type === "skandale").length;

    return (
        <div data-testid="articles-page">
            <Seo
                title="Artikler — dokumentation af løgne og valgflæsk"
                description="Kuraterede artikler fra DR, TV 2, Politiken, B.T. og Ekstra Bladet der dokumenterer danske politikeres løgne, løftebrud og valgflæsk. Opdateres dagligt."
                keywords="politiske artikler, danske nyheder, DR, TV2, Politiken, BT, Ekstra Bladet, faktatjek, løgne, valgflæsk"
                path="/artikler"
            />
            {/* HERO */}
            <section className="bg-royal-gradient text-white relative overflow-hidden grain">
                <div className="absolute inset-0 bg-grid opacity-20" />
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20 relative">
                    <div className="max-w-4xl">
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-xs uppercase tracking-[0.18em] mb-5">
                            <Newspaper className="w-3.5 h-3.5" />
                            Mediearkiv
                        </div>
                        <h1 className="font-serif-dm text-5xl md:text-6xl leading-[0.95] tracking-tight">
                            Når politikere <span className="text-[#F4C96B] italic">tages i løgne</span>
                            <span className="block text-3xl md:text-4xl text-white/80 mt-3 not-italic">
                                — eller havner i en skandale
                            </span>
                        </h1>
                        <p className="mt-5 text-lg text-white/80 max-w-2xl leading-relaxed">
                            Et kurateret overblik over medieomtale fra Danmarks største
                            nyhedsredaktioner — artikler, hvor politikere er fanget i
                            løgne, løftebrud, valgflæsk eller egentlige skandaler.
                        </p>
                        <div className="flex flex-wrap gap-4 mt-7">
                            <Pill icon={Flame} label="Skandaler" value={scandalCount} scandal />
                            <Pill icon={AlertTriangle} label="Dokumenterede løgne" value={lieCount} accent />
                            <Pill icon={Newspaper} label="Valgflæsk-sager" value={valgflaeskCount} />
                            <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/10 border border-white/15 text-xs">
                                <span className="w-1.5 h-1.5 rounded-full bg-[#7EE2A6] animate-pulse" />
                                Auto-opdatering kl. 12:00 dagligt
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* SOURCES LIST */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
                <span className="text-xs uppercase tracking-[0.2em] text-[#4F5A7A] font-semibold">
                    Kilder
                </span>
                <h2 className="font-serif-dm text-3xl text-royal mt-2 mb-5">
                    Vi trækker fra Danmarks førende politiske redaktioner
                </h2>
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                    <SourceTile
                        name="Ekstra Bladet"
                        url="https://ekstrabladet.dk"
                        description="Politisk journalistik, der går tæt på magten."
                        active={sourceKey === "Ekstra Bladet"}
                        onClick={() => setSourceKey(sourceKey === "Ekstra Bladet" ? "" : "Ekstra Bladet")}
                        testId="source-eb"
                    />
                    <SourceTile
                        name="B.T."
                        url="https://bt.dk"
                        description="Nyheder, politik og samfund i øjenhøjde."
                        active={sourceKey === "B.T."}
                        onClick={() => setSourceKey(sourceKey === "B.T." ? "" : "B.T.")}
                        testId="source-bt"
                    />
                    <SourceTile
                        name="TV 2 Politik"
                        url="https://nyheder.tv2.dk/politik"
                        description="Politisk dækning fra Danmarks største tv-nyhedsbureau."
                        active={sourceKey === "TV 2 Politik"}
                        onClick={() => setSourceKey(sourceKey === "TV 2 Politik" ? "" : "TV 2 Politik")}
                        testId="source-tv2"
                    />
                    <SourceTile
                        name="Politiken"
                        url="https://politiken.dk"
                        description="Dybdeborende politisk analyse og reportage."
                        active={sourceKey === "Politiken"}
                        onClick={() => setSourceKey(sourceKey === "Politiken" ? "" : "Politiken")}
                        testId="source-pol"
                    />
                    <SourceTile
                        name="DR Nyheder"
                        url="https://www.dr.dk/nyheder/politik"
                        description="Public service-journalistik og faktatjek af politikere."
                        active={sourceKey === "DR Nyheder"}
                        onClick={() => setSourceKey(sourceKey === "DR Nyheder" ? "" : "DR Nyheder")}
                        testId="source-dr"
                    />
                </div>
            </section>

            {/* FILTERS + LIST */}
            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="flex flex-col md:flex-row gap-3 mb-6">
                    <select
                        value={type}
                        onChange={(e) => setType(e.target.value)}
                        className="px-5 py-3 border border-[#E5E8F0] rounded-full text-sm bg-white focus:outline-none focus:border-royal flex-1"
                        data-testid="article-filter-type"
                    >
                        {TYPES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                        ))}
                    </select>
                    <select
                        value={sourceKey}
                        onChange={(e) => setSourceKey(e.target.value)}
                        className="px-5 py-3 border border-[#E5E8F0] rounded-full text-sm bg-white focus:outline-none focus:border-royal flex-1"
                        data-testid="article-filter-source"
                    >
                        {SOURCES.map((s) => (
                            <option key={s.key} value={s.key}>{s.label}</option>
                        ))}
                    </select>
                    <select
                        value={partyId}
                        onChange={(e) => setPartyId(e.target.value)}
                        className="px-5 py-3 border border-[#E5E8F0] rounded-full text-sm bg-white focus:outline-none focus:border-royal flex-1"
                        data-testid="article-filter-party"
                    >
                        <option value="">Alle partier</option>
                        {parties.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                        ))}
                    </select>
                </div>

                <div className="text-sm text-[#4F5A7A] mb-5" data-testid="articles-count">
                    Viser {filtered.length} artikler
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                    {filtered.map((a) => (
                        <ArticleCard
                            key={a.id}
                            article={a}
                            partyName={partiesById[a.party_id]?.name}
                        />
                    ))}
                </div>

                {filtered.length === 0 && (
                    <div className="text-center py-20 text-[#4F5A7A]" data-testid="no-articles">
                        Ingen artikler matcher dine filtre.
                    </div>
                )}

                <p className="mt-10 text-xs text-[#8A94AE] italic max-w-3xl">
                    Løftebarometeret er en uafhængig aggregator. Alle artikler åbner på
                    den oprindelige kildes hjemmeside. Vi ejer ikke indholdet og tjener
                    ingen penge på klik.
                </p>
            </section>
        </div>
    );
}

function Pill({ icon: Icon, label, value, accent, scandal }) {
    let cls = "bg-white/10 border border-white/15";
    if (scandal) cls = "bg-[#6B0F8C]/30 border border-[#6B0F8C]/60";
    else if (accent) cls = "bg-[#BB1B2D]/20 border border-[#BB1B2D]/40";
    return (
        <div className={`inline-flex items-center gap-3 px-4 py-2.5 rounded-full ${cls}`} data-testid={`articles-stat-${(label || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
            <Icon className="w-4 h-4" />
            <div>
                <div className="font-serif-dm text-xl leading-none">{value}</div>
                <div className="text-[10px] uppercase tracking-wider text-white/70">{label}</div>
            </div>
        </div>
    );
}

function SourceTile({ name, url, description, active, onClick, testId }) {
    return (
        <button
            onClick={onClick}
            data-testid={testId}
            className={`text-left p-5 rounded-xl border-2 transition-all hover-lift bg-white ${
                active ? "border-royal bg-royal-soft" : "border-[#E5E8F0] hover:border-royal/40"
            }`}
        >
            <div className="flex items-center justify-between mb-2">
                <h3 className="font-serif-dm text-xl text-royal">{name}</h3>
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="text-xs text-royal hover:text-danish-red underline"
                    data-testid={`${testId}-external`}
                >
                    Besøg
                </a>
            </div>
            <p className="text-sm text-[#4F5A7A]">{description}</p>
        </button>
    );
}
